// frontend/src/components/CognitionAddModal.js
// Modal component for adding cognition data directly in the flowsheet

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Input,
  Space,
  Typography,
  Alert,
  message,
  Divider
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { usePatientChart } from '../context/PatientChartContext';
import MomentDatePicker from './MomentDatePicker.tsx';

const { Title, Text } = Typography;

/**
 * CognitionAddModal Component
 * Provides a dedicated interface for adding cognition data to the flowsheet
 */
const CognitionAddModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment());
  const { refreshVitals } = usePatientChart();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedDateTime(moment());
    }
  }, [visible, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form fields
      const values = await form.validateFields();
      
      // Filter out empty values
      const filteredValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          filteredValues[key] = values[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one cognition parameter.');
        return;
      }

      // Prepare payload for backend - using the biometrics endpoint
      const payload = {
        entryDate: selectedDateTime.toISOString(),
        biometrics: filteredValues
      };

      // Call backend API using the existing biometrics endpoint
      const response = await axios.post(`/patients/${patientId}/flowsheet/biometrics`, payload);

      if (response.data.status === 'success') {
        message.success('Cognition data saved successfully!');

        // Refresh vitals banner (in case cognition data affects displayed metrics)
        refreshVitals();

        // Call parent onSave callback to refresh flowsheet
        if (onSave) {
          onSave();
        }

        // Close modal and reset form
        onClose();
        form.resetFields();
      } else {
        throw new Error(response.data.message || 'Failed to save cognition data');
      }
    } catch (error) {
      console.error('Error saving cognition data:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to save cognition data');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Define cognition parameters based on parameters.md
  const cognitionFields = [
    { name: 'biometrics_cognition_tmtA_time', label: 'Trail Making Test A - Time', units: 'seconds', inputType: 'InputNumber', placeholder: 'seconds', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_tmtA_errors', label: 'Trail Making Test A - Errors', inputType: 'InputNumber', placeholder: 'errors', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_tmtB_time', label: 'Trail Making Test B - Time', units: 'seconds', inputType: 'InputNumber', placeholder: 'seconds', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_tmtB_errors', label: 'Trail Making Test B - Errors', inputType: 'InputNumber', placeholder: 'errors', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_wordRecall_immediate1', label: 'Word Recall - Immediate Trial 1', inputType: 'InputNumber', placeholder: '/10', rules: [{ type: 'number', min: 0, max: 10, message: 'Score must be between 0 and 10' }] },
    { name: 'biometrics_cognition_wordRecall_immediate2', label: 'Word Recall - Immediate Trial 2', inputType: 'InputNumber', placeholder: '/10', rules: [{ type: 'number', min: 0, max: 10, message: 'Score must be between 0 and 10' }] },
    { name: 'biometrics_cognition_wordRecall_delayed', label: 'Word Recall - Delayed', inputType: 'InputNumber', placeholder: '/10', rules: [{ type: 'number', min: 0, max: 10, message: 'Score must be between 0 and 10' }] },
    { name: 'biometrics_cognition_stroop_word_time', label: 'Stroop Test - Word Time', units: 'seconds', inputType: 'InputNumber', placeholder: 'seconds', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_stroop_word_errors', label: 'Stroop Test - Word Errors', inputType: 'InputNumber', placeholder: 'errors', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_stroop_color_time', label: 'Stroop Test - Color Time', units: 'seconds', inputType: 'InputNumber', placeholder: 'seconds', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_stroop_color_errors', label: 'Stroop Test - Color Errors', inputType: 'InputNumber', placeholder: 'errors', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_stroop_incongruent_time', label: 'Stroop Test - Incongruent Time', units: 'seconds', inputType: 'InputNumber', placeholder: 'seconds', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_stroop_incongruent_errors', label: 'Stroop Test - Incongruent Errors', inputType: 'InputNumber', placeholder: 'errors', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_stroop_interference_time', label: 'Stroop Test - Interference Time', units: 'seconds', inputType: 'InputNumber', placeholder: 'seconds', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_cognition_stroop_interference_errors', label: 'Stroop Test - Interference Errors', inputType: 'InputNumber', placeholder: 'errors', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
  ];

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined />
          Add Cognition Data
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={submitLoading}
          onClick={handleSave}
          style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
        >
          Save Cognition Data
        </Button>,
      ]}
      width={800}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Add New Cognition Entry"
          description="Enter cognitive assessment results and neuropsychological test scores for the selected date and time. These assessments help track cognitive function changes over time."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Date/Time Selection */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Text strong>Entry Date & Time:</Text>
            <br />
            <MomentDatePicker
              showTime
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select date and time"
            />
          </Col>
        </Row>
      </div>

      <Form form={form} layout="vertical" name="cognition_add_form">
        <Title level={5}>Cognitive Assessment Parameters</Title>
        
        {/* Trail Making Tests */}
        <Divider orientation="left">Trail Making Tests</Divider>
        <Row gutter={16}>
          {cognitionFields.slice(0, 4).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={field.placeholder}
                  min={field.rules?.[0]?.min}
                  max={field.rules?.[0]?.max}
                  precision={1}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Word Recall Tests */}
        <Divider orientation="left">Word Recall Tests</Divider>
        <Row gutter={16}>
          {cognitionFields.slice(4, 7).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={field.placeholder}
                  min={field.rules?.[0]?.min}
                  max={field.rules?.[0]?.max}
                  precision={1}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Stroop Tests */}
        <Divider orientation="left">Stroop Tests</Divider>
        <Row gutter={16}>
          {cognitionFields.slice(7).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={field.placeholder}
                  min={field.rules?.[0]?.min}
                  max={field.rules?.[0]?.max}
                  precision={1}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form>
    </Modal>
  );
};

export default CognitionAddModal;
